import {
  FulfillmentMerchant,
  FulfillmentMerchantCode,
  FulfillmentMerchantApi,
  FulfillmentMerchantsResponse,
  FulfillmentPriorityResponse,
  FulfillmentPriorityShop,
  FulfillmentPriorityShopApi,
  FulfillmentPriorityUpdateShop,
} from "../@types/FulfillmentPriority";

function getMerchantsFromResponse(response: FulfillmentMerchantsResponse) {
  if (Array.isArray(response)) return response;
  return response.merchants ?? response.content ?? [];
}

const FULFILLMENT_MERCHANT_CODES = {
  FFS: "SBC",
  SHOPIFY: ["INCYCLE", "SANDBOX"],
} as const;

const FULFILLMENT_MERCHANT_LABELS: Record<string, string> = {
  INCYCLE: "Shopify",
  SANDBOX: "Shopify",
};

function normalizeMerchantCode(merchantCode: string) {
  return merchantCode.trim().toLocaleUpperCase();
}

export function getFulfillmentMerchantLabel(
  merchant: FulfillmentMerchant,
  ffsLabel: string,
) {
  const merchantCode = normalizeMerchantCode(merchant.merchantCode);

  if (merchantCode === FULFILLMENT_MERCHANT_CODES.FFS) return ffsLabel;

  return FULFILLMENT_MERCHANT_LABELS[merchantCode] ?? merchant.name;
}

function getFulfillmentMerchantOrder(merchant: FulfillmentMerchant) {
  if (isShopifyMerchant(merchant)) return 0;

  return normalizeMerchantCode(merchant.merchantCode) ===
    FULFILLMENT_MERCHANT_CODES.FFS
    ? 1
    : 2;
}

function isShopifyMerchant(merchant: FulfillmentMerchant) {
  const merchantCode = normalizeMerchantCode(merchant.merchantCode);

  return FULFILLMENT_MERCHANT_CODES.SHOPIFY.some(
    (shopifyCode) => shopifyCode === merchantCode,
  );
}

export function normalizeFulfillmentMerchants(
  response: FulfillmentMerchantsResponse,
): FulfillmentMerchant[] {
  const merchants = getMerchantsFromResponse(response)
    .filter(
      (
        merchant,
      ): merchant is FulfillmentMerchantApi & {
        merchantCode: string;
        name: string;
      } => Boolean(merchant.merchantCode?.trim() && merchant.name?.trim()),
    )
    .map(({ merchantCode, name }) => ({ merchantCode, name }));

  return merchants.sort(
    (first, second) =>
      getFulfillmentMerchantOrder(first) - getFulfillmentMerchantOrder(second),
  );
}

export function getDefaultFulfillmentMerchantCode(
  merchants: FulfillmentMerchant[],
): FulfillmentMerchantCode {
  const shopifyMerchant = merchants.find(isShopifyMerchant);

  return shopifyMerchant?.merchantCode ?? merchants[0]?.merchantCode ?? "";
}

export function getFulfillmentPriorityShopKey(
  shop: Pick<FulfillmentPriorityShop, "shopID" | "tenantName">,
) {
  return `${shop.tenantName}:${shop.shopID}`;
}

export function haveFulfillmentPriorityShopsChanged(
  originalShops: FulfillmentPriorityShop[],
  currentShops: FulfillmentPriorityShop[],
) {
  if (originalShops.length !== currentShops.length) return true;

  return originalShops.some((originalShop, index) => {
    const currentShop = currentShops[index];

    return (
      getFulfillmentPriorityShopKey(originalShop) !==
        getFulfillmentPriorityShopKey(currentShop) ||
      originalShop.priority !== currentShop.priority ||
      originalShop.isActive !== currentShop.isActive
    );
  });
}

function getShopsFromResponse(response: FulfillmentPriorityResponse) {
  if (Array.isArray(response)) return response;
  return response.shops ?? response.content ?? [];
}

function normalizeIsActive(value: FulfillmentPriorityShopApi["isActive"]) {
  return value === true || value === "true";
}

export function normalizeFulfillmentPriorityShops(
  response: FulfillmentPriorityResponse,
): FulfillmentPriorityShop[] {
  return getShopsFromResponse(response)
    .filter(
      (
        shop,
      ): shop is FulfillmentPriorityShopApi & {
        shopID: string | number;
        tenantName: string;
      } => shop.shopID !== undefined && Boolean(shop.tenantName),
    )
    .map((shop) => ({
      shopID: String(shop.shopID),
      tenantName: shop.tenantName,
      priority: Number(shop.priority) || 0,
      isActive: normalizeIsActive(shop.isActive),
      name: shop.name || shop.shopName || String(shop.shopID),
    }))
    .sort((first, second) => first.priority - second.priority);
}

export function buildFulfillmentPriorityPayload(
  shops: FulfillmentPriorityShop[],
  merchantCode: FulfillmentMerchantCode,
): FulfillmentPriorityUpdateShop[] {
  return shops.map(({ shopID, tenantName, priority, isActive }) => ({
    merchantCode,
    tenantName,
    shopID,
    isActive,
    priority,
  }));
}

export function reorderFulfillmentPriorityShops(
  shops: FulfillmentPriorityShop[],
  activeKey: string,
  overKey: string,
  preservePrioritySlots = false,
) {
  const fromIndex = shops.findIndex(
    (shop) => getFulfillmentPriorityShopKey(shop) === activeKey,
  );
  const toIndex = shops.findIndex(
    (shop) => getFulfillmentPriorityShopKey(shop) === overKey,
  );

  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return shops;

  const reordered = [...shops];
  const [movedShop] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, movedShop);

  const prioritySlots = preservePrioritySlots
    ? shops
        .map(({ priority }) => priority)
        .sort((first, second) => first - second)
    : reordered.map((_, index) => index + 1);

  return reordered.map((shop, index) => ({
    ...shop,
    priority: prioritySlots[index],
  }));
}

export interface FulfillmentPriorityAccess {
  canAccess: boolean;
  isReadOnly: boolean;
}

interface CanSaveFulfillmentPriorityParams {
  isReadOnly: boolean;
  hasPendingChanges: boolean;
  saving: boolean;
  filterLoading: boolean;
}

export function canSaveFulfillmentPriority({
  isReadOnly,
  hasPendingChanges,
  saving,
  filterLoading,
}: CanSaveFulfillmentPriorityParams) {
  return !isReadOnly && hasPendingChanges && !saving && !filterLoading;
}

interface GetFulfillmentPriorityAccessParams {
  hasEditPermission: boolean;
  hasViewPermission: boolean;
  isUnitedStates: boolean;
}

/**
 * The view permission grants read-only access; the edit permission grants full
 * access and takes precedence when both are granted.
 */
export function getFulfillmentPriorityAccess({
  hasEditPermission,
  hasViewPermission,
  isUnitedStates,
}: GetFulfillmentPriorityAccessParams): FulfillmentPriorityAccess {
  const canAccess = isUnitedStates && (hasEditPermission || hasViewPermission);

  return { canAccess, isReadOnly: canAccess && !hasEditPermission };
}
